// CertificateSplit.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  parseCertificate,
  formatCertificateString,
  getAllSubSeries,
} from './certificateParser';

const seriesOptions = ["І", "1", "ІІ"];
const allSubSeriesOptions = getAllSubSeries();

function CertificateSplit({
  id = new Date().getTime(),
  value,
  setValue,
  error,
  label = "Серія та номер свідоцтва",
  placeholder = "576234",
  readOnly = false
}) {

  const readOnlyStyle = readOnly ? { backgroundColor: '#f8f8f8', pointerEvents: 'none' } : {};

  const [certData, setCertData] = useState(() => parseCertificate(value));
  const [subSeriesInput, setSubSeriesInput] = useState(() => parseCertificate(value).subSeries);
  const [showSubSeriesDropdown, setShowSubSeriesDropdown] = useState(false);

  const subseriesRef = useRef(null);
  // Зберігаємо останнє значення яке ми самі сформували,
  // щоб не реагувати на власні зміни в useEffect
  const prevValueRef = useRef(value);

  // Фільтрація варіантів підсерії за пошуковим запитом
  const filteredSubSeries = useMemo(() => {
    if (!subSeriesInput) return allSubSeriesOptions;
    return allSubSeriesOptions.filter(option =>
      option.startsWith(subSeriesInput)
    );
  }, [subSeriesInput]);

  const displayedSubSeries = filteredSubSeries.slice(0, 100);

  // Формуємо вихідний рядок і викликаємо setValue
  const emitValue = (series, subSeries, number) => {
    const newValue = formatCertificateString(series, subSeries, number);
    prevValueRef.current = newValue;
    setValue(newValue);
  };

  const handleSeriesChange = (e) => {
    const newSeries = e.target.value;
    setCertData(prev => ({ ...prev, series: newSeries }));
    emitValue(newSeries, certData.subSeries, certData.number);
  };

  const handleSubSeriesSelect = (selected) => {
    setCertData(prev => ({ ...prev, subSeries: selected }));
    setSubSeriesInput(selected);
    setShowSubSeriesDropdown(false);
    emitValue(certData.series, selected, certData.number);
  };

  const handleSubSeriesInputChange = (e) => {
    let val = e.target.value.toUpperCase().slice(0, 2);
    setSubSeriesInput(val);
    setShowSubSeriesDropdown(true);

    // Очищаємо підсерію в даних якщо поле порожнє
    if (val === '') {
      setCertData(prev => ({ ...prev, subSeries: '' }));
      emitValue(certData.series, '', certData.number);
    }
  };

  const handleSubSeriesBlur = () => {
    setTimeout(() => {
      setShowSubSeriesDropdown(false);

      // Приймаємо введене значення якщо воно валідне (2 укр. літери)
      if (subSeriesInput.length === 2 && allSubSeriesOptions.includes(subSeriesInput)) {
        setCertData(prev => ({ ...prev, subSeries: subSeriesInput }));
        emitValue(certData.series, subSeriesInput, certData.number);
      } else if (subSeriesInput === '') {
        setCertData(prev => ({ ...prev, subSeries: '' }));
        emitValue(certData.series, '', certData.number);
      } else {
        // Повертаємо попереднє валідне значення
        setSubSeriesInput(certData.subSeries);
      }
    }, 200);
  };

  const handleNumberChange = (e) => {
    const num = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCertData(prev => ({ ...prev, number: num }));
    emitValue(certData.series, certData.subSeries, num);
  };

  // Синхронізація тільки при зовнішній зміні value (напр. завантаження з сервера, reset)
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      const parsed = parseCertificate(value);
      setCertData(parsed);
      setSubSeriesInput(parsed.subSeries);
    }
  }, [value]);

  // Закриття дропдауну при кліку зовні
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (subseriesRef.current && !subseriesRef.current.contains(e.target)) {
        setShowSubSeriesDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="form-row-plain">
      <div className="form-group" style={{ width: '100%' }}>
        <label className="form-label">
          {label}{error && <span className="text-danger"> *</span>}
        </label>
        <div className="date-inline" style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>

          {/* Серія: І, 1, ІІ */}
          <select
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={certData.series}
            id={id + 'series'}
            onChange={handleSeriesChange}
            disabled={readOnly}
            style={{ width: '60px', flexShrink: 0, ...readOnlyStyle }}
          >
            {seriesOptions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>

          <span style={{ fontSize: '18px', fontWeight: 'bold', flexShrink: 0 }}>-</span>

          {/* Підсерія: input з дропдауном */}
          <div
            ref={subseriesRef}
            style={{ position: 'relative', flexShrink: 0, width: '90px' }}
          >
            <input
              type="text"
              className={`form-control ${error ? 'is-invalid' : ''}`}
              value={subSeriesInput}
              onChange={handleSubSeriesInputChange}
              onFocus={() => !readOnly && setShowSubSeriesDropdown(true)}
              onBlur={handleSubSeriesBlur}
              placeholder="БК"
              style={{ width: '100%', textTransform: 'uppercase', ...readOnlyStyle }}
              autoComplete="off"
              maxLength={2}
              disabled={readOnly}
            />
            {showSubSeriesDropdown && filteredSubSeries.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {displayedSubSeries.map(option => (
                  <div
                    key={option}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSubSeriesSelect(option);
                    }}
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {option}
                  </div>
                ))}
                {filteredSubSeries.length > 100 && (
                  <div style={{ padding: '8px', color: '#999', textAlign: 'center', fontSize: '12px' }}>
                    ще {filteredSubSeries.length - 100}...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Номер: 6 цифр */}
          <input
            type="text"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            placeholder={placeholder}
            id={id + 'number'}
            value={certData.number}
            onChange={handleNumberChange}
            disabled={readOnly}
            style={{ width: '120px', flexShrink: 0, ...readOnlyStyle }}
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
          />
        </div>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    </div>
  );
}

export default CertificateSplit;